import {ConfigManager} from '@pexip/config-manager/ConfigManager';
import {configHook} from '@pexip/config-manager/configHook';
import type {MediaDeviceInfoLike} from '@pexip/media-control';
import {
    CURSOR_CAPTURE_CONSTRAINT,
    DISPLAY_CAPTURE_SURFACE_TYPE,
    INCLUDE_EXCLUDE_CONSTRAINT,
} from '@pexip/media-control';
import {AUDIO_CONTENT_HINTS, VIDEO_CONTENT_HINTS} from '@pexip/media';
import {getCanUseWebGL} from '@pexip/media-processor';
import {
    useAssertedContext,
    currentBrowserName,
    isMobileDevice,
    getUserAgentDetails,
} from '@pexip/media-components';

import {
    ALLOW_CHANGE_OF_MEDIA_DIRECTION,
    BACKGROUND_BLUR_AMOUNT,
    BACKGROUND_THRESHOLD,
    BG_IMAGE_URL,
    CALL_TYPE,
    DOWN_SAMPLE_FACTOR,
    EDGE_BLUR_AMOUNT,
    EXCLUDE_BYSTANDERS,
    FOREGROUND_THRESHOLD,
    INITIAL_BANDWIDTH,
    LIGHT_WRAP_BLUR_AMOUNT,
    LIGHT_WRAP_EDGE_BAND,
    LIGHT_WRAP_INTENSITY,
    LIGHT_WRAP_TIGHTNESS,
    MASK_COMBINE_RATIO,
    MORPH_DILATE_RADIUS_PX,
    MORPH_ERODE_RADIUS_PX,
    MORPH_PASS,
    PERSON_CENTER,
    PREFER_PRES_IN_MIX,
    RENDER_EFFECTS,
    SIGMA_RANGE,
    SIGMA_SPACE,
} from './constants';
import {
    BrowserCloseConfirmation,
    StepByStepCompletion,
    type AppConfig,
    type DefaultUserConfig,
    type InternalConifg,
} from './types';
import {ConfigContext} from './contexts/config.context';
import {isChromiumWindows} from './utils/userAgentData';

export const shouldEnableVideoProcessing = () => !isMobileDevice();
export const shouldEnableAudioProcessing = () => !isMobileDevice();
export const shouldEnableAudioTuneables = () =>
    !currentBrowserName?.includes('Safari');
const supportWebGL2 = getCanUseWebGL();

const getGPUAPI = () => {
    if (!supportWebGL2) {
        return 'canvas2d';
    }
    // WebGPU API might causes alternating the muted events for Windows users
    // Firefox supports WebGPU, but not all the features our implementation depends on
    return isChromiumWindows() ||
        currentBrowserName === 'Firefox' ||
        getUserAgentDetails(navigator.userAgent).isIOS // WebGPU iOS composite rotated frame
        ? 'webgl'
        : 'webgpu';
};

export const internalConfig: InternalConifg = {
    asd: false,
    // FIXME: We should not use empty object as a default value for audioInput, audioOutput and videoInput
    audioInput: {} as MediaDeviceInfoLike,
    audioOutput: {} as MediaDeviceInfoLike,
    lastFallBackMsg: '',
    showSelfviewHiddenTooltip: true,
    vad: false,
    videoInput: {} as MediaDeviceInfoLike,
    stepByStepCompletion: StepByStepCompletion.Uncompleted,
};

export const defaultUserConfig: DefaultUserConfig = {
    allowChangeOfMediaDirection: ALLOW_CHANGE_OF_MEDIA_DIRECTION,
    audioContentHint: AUDIO_CONTENT_HINTS.Speech,
    autoGainControl: shouldEnableAudioTuneables(),
    backgroundBlurAmount: BACKGROUND_BLUR_AMOUNT,
    bandwidth: INITIAL_BANDWIDTH,
    allowVP9: true,
    bgImageUrl: BG_IMAGE_URL,
    callType: CALL_TYPE,
    cursorCapture: CURSOR_CAPTURE_CONSTRAINT.Always,
    denoise: false,
    deviceAlias: '',
    displayName: '',
    isGuest: false,
    displaySurface: DISPLAY_CAPTURE_SURFACE_TYPE.Window,
    echoCancellation: true,
    edgeBlurAmount: EDGE_BLUR_AMOUNT,
    fecc: false,
    maskCombineRatio: MASK_COMBINE_RATIO,
    foregroundThreshold: FOREGROUND_THRESHOLD,
    backgroundThreshold: BACKGROUND_THRESHOLD,
    downSampleFactor: DOWN_SAMPLE_FACTOR,
    excludeBystanders: EXCLUDE_BYSTANDERS,
    lightWrapBlurAmount: LIGHT_WRAP_BLUR_AMOUNT,
    lightWrapEdgeBand: LIGHT_WRAP_EDGE_BAND,
    lightWrapIntensity: LIGHT_WRAP_INTENSITY,
    lightWrapTightness: LIGHT_WRAP_TIGHTNESS,
    morphDilateRadiusPx: MORPH_DILATE_RADIUS_PX,
    morphErodeRadiusPx: MORPH_ERODE_RADIUS_PX,
    morphPass: MORPH_PASS,
    personCenterX: PERSON_CENTER[0],
    personCenterY: PERSON_CENTER[1],
    sigmaRangeHi: SIGMA_RANGE[1],
    sigmaRangeLo: SIGMA_RANGE[0],
    sigmaSpace: SIGMA_SPACE,
    isAudioInputMuted: false,
    isUserFacing: true,
    isVideoInputMuted: false,
    noiseSuppression: shouldEnableAudioTuneables(),
    presoContentHint: VIDEO_CONTENT_HINTS.Detail,
    segmentationEffects: RENDER_EFFECTS,
    showSelfView: true,
    skipDisplayNamePrompt: false,
    surfaceSwitching: INCLUDE_EXCLUDE_CONSTRAINT.Include,
    userBgImageAssets: [] as string[],
    videoContentHint: VIDEO_CONTENT_HINTS.NoHint,
    videoProcessingAPI: 'stream',
    videoProcessingDelegate: supportWebGL2 ? 'GPU' : 'CPU',
    gpuAPI: getGPUAPI(),
    captureAudio: true,
    join: false,
    preferPresInMix: PREFER_PRES_IN_MIX,
    mirrorSelfview: true,
    enableBrowserCloseConfirmationByDefault: false,
    browserCloseConfirmation: BrowserCloseConfirmation.Unset,
    enforceAudioMute: false,
    enforceVideoMute: false,
    // If the user is on a touch device we want to always show UI controls by default, not auto hide them,
    // because mobile screens readers need to be able to access the controls
    alwaysDisplayUIInterfaces:
        'matchMedia' in window
            ? window.matchMedia('(pointer: coarse)').matches
            : true,
    showParticipantAuthenticatedState: false,
    disableKeyboardShortcuts: false,
    stopCameraAsMute: currentBrowserName !== 'Firefox',
    favoritesOrder: {meetingIds: []},
    autoPictureInPicture: false,
};

export const config = new ConfigManager<AppConfig>(
    {
        ...internalConfig,
        ...defaultUserConfig,
    },
    'infinity-connect',
);

export const useConfigContext = () => useAssertedContext(ConfigContext);

export const useConfig = <T extends keyof AppConfig>(key: T) =>
    configHook(useConfigContext())(key);
